import { CompliancePage } from './pages/CompliancePage.jsx';
import { PricingPage } from './pages/pricing/PricingPage.jsx';
import StatsPage from './pages/StatsPage.jsx';
import { NewBookingPage } from './pages/bookings/NewBookingPage.jsx';
import { Routes, Route, Navigate } from 'react-router-dom';
import { SignedIn, SignedOut, useAuth } from '@clerk/clerk-react';
import * as Sentry from '@sentry/react';
import { SignInPage } from './pages/SignInPage.jsx';
import { AppShell } from './components/AppShell.jsx';
import { DashboardPage } from './pages/DashboardPage.jsx';
import { NotProvisionedPage } from './pages/NotProvisionedPage.jsx';
import { NotFoundPage } from './pages/NotFoundPage.jsx';
import { ChauffeursListPage } from './pages/chauffeurs/ChauffeursListPage.jsx';
import { ChauffeurFormPage } from './pages/chauffeurs/ChauffeurFormPage.jsx';
import { ChauffeurDetailPage } from './pages/chauffeurs/ChauffeurDetailPage.jsx';
import { ChauffeurStatementsPage } from './pages/chauffeurs/ChauffeurStatementsPage.jsx';
import { StatementDetailPage } from './pages/chauffeurs/StatementDetailPage.jsx';
import { VehiclesListPage } from './pages/vehicles/VehiclesListPage.jsx';
import { VehicleFormPage } from './pages/vehicles/VehicleFormPage.jsx';
import { VehicleDetailPage } from './pages/vehicles/VehicleDetailPage.jsx';
import { CustomersListPage } from './pages/customers/CustomersListPage.jsx';
import { CustomerFormPage } from './pages/customers/CustomerFormPage.jsx';
import { CustomerDetailPage } from './pages/customers/CustomerDetailPage.jsx';
import BookingsListPage from './pages/bookings/BookingsListPage.jsx';
import BookingDetailPage from './pages/bookings/BookingDetailPage.jsx';
import QuotesListPage from './pages/bookings/QuotesListPage.jsx';
import QuoteDetailPage from './pages/bookings/QuoteDetailPage.jsx';
import { StubPage } from './pages/StubPage.jsx';
import AuditLogPage from './pages/AuditLogPage.jsx';
import { useCurrentUser } from './hooks/useCurrentUser.js';
import MessagesPage from './pages/messages/MessagesPage.jsx';
import { ReportsPage } from './pages/reports/ReportsPage.jsx';
import { SalesExecStatementDetailPage } from './pages/reports/SalesExecStatementDetailPage.jsx';
const SentryRoutes = Sentry.withSentryReactRouterV6Routing
    ? Sentry.withSentryReactRouterV6Routing(Routes)
    : Routes;
export default function App() {
    const { isLoaded } = useAuth();

    // Wait for Clerk to finish initialising before deciding which gate to
    // render. Without this, the first render happens while Clerk's session
    // state is still resolving, both <SignedIn> and <SignedOut> render
    // nothing, then state resolves and <Navigate to="/sign-in"> fires.
    // Moments later Clerk re-evaluates and the component briefly unmounts +
    // remounts — which causes the <SignIn> component to fire its
    // "send verification code" request TWICE. Result: two emails, only
    // the second code valid (Clerk invalidates earlier ones on a fresh
    // attempt) and a visible flash between email→code steps.
    if (!isLoaded) {
        return <FullScreenLoader />;
    }

    return (
<SentryRoutes>
            <Route path="/sign-in/*" element={<SignInPage />} />
            <Route path="*" element={
                <>
                    <SignedOut><Navigate to="/sign-in" replace /></SignedOut>
                    <SignedIn><AuthenticatedRoutes /></SignedIn>
                </>
            } />
        </SentryRoutes>
    );
}
function AuthenticatedRoutes() {
    const { isLoading, isError, error } = useCurrentUser();
    if (isLoading) return <FullScreenLoader />;
    if (isError && error?.statusCode === 403) return <NotProvisionedPage />;
    if (isError) return <FullScreenError message={error?.message || 'Something went wrong'} />;
    return (
        <AppShell>
            <Routes>
                <Route path="/" element={<DashboardPage />} />
                {/* Chauffeurs */}
                <Route path="/chauffeurs" element={<ChauffeursListPage />} />
                <Route path="/chauffeurs/new" element={<ChauffeurFormPage />} />
                <Route path="/chauffeurs/:id" element={<ChauffeurDetailPage />} />
                <Route path="/chauffeurs/:id/edit" element={<ChauffeurFormPage />} />
                <Route path="/chauffeurs/:id/statements" element={<ChauffeurStatementsPage />} />
                <Route path="/statements/:id" element={<StatementDetailPage />} />
                {/* Vehicles */}
                <Route path="/vehicles" element={<VehiclesListPage />} />
                <Route path="/vehicles/new" element={<VehicleFormPage />} />
                <Route path="/vehicles/:id" element={<VehicleDetailPage />} />
                <Route path="/vehicles/:id/edit" element={<VehicleFormPage />} />
                 {/* Customers */}
                <Route path="/customers" element={<CustomersListPage />} />
                <Route path="/customers/new" element={<CustomerFormPage />} />
                <Route path="/customers/:id" element={<CustomerDetailPage />} />
                <Route path="/customers/:id/edit" element={<CustomerFormPage />} />
                {/* Bookings */}
                <Route path="/bookings" element={<BookingsListPage />} />
                <Route path="/bookings/new" element={<NewBookingPage />} />
                <Route path="/bookings/:id" element={<BookingDetailPage />} />

                {/* Quotes — same family as bookings, accessed via the
                    Bookings|Quotes toggle on the bookings list page. */}
                <Route path="/quotes" element={<QuotesListPage />} />
                <Route path="/quotes/:id" element={<QuoteDetailPage />} />

                {/* Compliance — owner-only reports */}
                                <Route path="/compliance" element={<CompliancePage />} />

                {/* Reports — statements, reports, printouts */}
                <Route path="/reports" element={<ReportsPage />} />
                <Route path="/reports/sales-exec-statements/:id" element={<SalesExecStatementDetailPage />} />

                {/* Pricing — owner-only commercial config (discounts, fixed routes, etc.) */}
                <Route path="/pricing" element={<PricingPage />} />

                {/* Stats — owner-only ops dashboard */}
                               <Route path="/stats" element={<StatsPage />} />
                <Route path="/messages" element={<MessagesPage />} />

                {/* Audit log — UI not built yet, stub until then */}
                <Route path="/audit" element={<AuditLogPage />} />

                <Route path="*" element={<NotFoundPage />} />
            </Routes>
        </AppShell>
    );
}
function FullScreenLoader() {
    return (
        <div className="min-h-screen flex items-center justify-center">
            <div className="text-sm text-ink-subtle">Loading…</div>
        </div>
    );
}
function FullScreenError({ message }) {
    return (
        <div className="min-h-screen flex items-center justify-center p-6">
            <div className="max-w-md text-center">
                <div className="text-sm font-medium text-status-danger mb-2">Error</div>
                <div className="text-ink-muted text-sm">{message}</div>
            </div>
        </div>
    );
}
